import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { ArrowRight, TrendingUp, CheckCircle2 } from 'lucide-react';
import { servicesSeedData } from '../../database/seedData/services';

const DataAdvisory = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const service = servicesSeedData.find(s => s.slug === 'data-advisory');

  if (!service) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-24`}>
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className={`text-5xl font-bold mb-8 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Service Not Found</h1>
          <button onClick={() => navigate('/services')} className={`px-10 py-4 rounded-xl text-xl font-semibold ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}>View All Services</button>
        </div>
      </div>
    );
  }

  // Executive metrics and capabilities removed - no fabricated data

  const timelineEntries = service.timeline
    ? [
        { title: 'Discovery', description: service.timeline.discovery },
        { title: service.timeline.design ? 'Design' : service.timeline.strategy ? 'Strategy' : 'Pilot', description: service.timeline.design || service.timeline.strategy || service.timeline.pilot },
        { title: service.timeline.implementation ? 'Implementation' : 'Production', description: service.timeline.implementation || service.timeline.production }
      ].filter((entry) => entry.description)
    : [];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
      {/* Hero - Executive Boardroom Style */}
      <div className={`relative ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'} overflow-hidden`}>
        {/* Accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-cyan-400 via-teal-500 to-cyan-600"></div>

        <div className="max-w-7xl mx-auto px-8 py-24 lg:py-32">
          <div className="flex justify-between items-center mb-12">
            <button
              onClick={() => navigate('/services')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-base font-semibold bg-white/10 text-white border border-white/30 hover:bg-white/20 transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Services
            </button>
            <span className="text-sm uppercase tracking-[0.4em] text-cyan-400/80">
              Shared Oxygen Advisory
            </span>
          </div>
          <div className="max-w-4xl">
            {/* Eyebrow text */}
            <div className="flex items-center mb-6">
              <div className="h-px w-12 bg-cyan-500 mr-4"></div>
              <span className="text-cyan-400 uppercase tracking-widest text-sm font-semibold">
                Data Architecture & Strategy
              </span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              {service.title}
            </h1>
            
            <p className="text-2xl lg:text-3xl text-gray-200 font-normal leading-relaxed mb-12">
              {service.shortDescription}
            </p>

            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center px-12 py-5 bg-cyan-500 hover:bg-cyan-600 text-gray-900 text-lg font-bold rounded-none transition-all duration-300"
            >
              Schedule Executive Briefing
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Executive Summary - Premium card style */}
      <div className={`${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-20`}>
        <div className="max-w-6xl mx-auto px-8">
          <div className={`border-2 p-12 ${theme === 'dark' ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-start mb-6">
              <div className="h-1 w-16 bg-cyan-500 mt-3 mr-6"></div>
              <div>
                <h2 className={`text-sm uppercase tracking-widest font-bold mb-4 ${theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  Executive Summary
                </h2>
                <p className={`text-xl lg:text-2xl leading-relaxed ${theme === 'dark' ? 'text-slate-200' : 'text-gray-900'}`}>
                  {service.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics section removed - no fabricated data */}

      {/* Capabilities removed - redundant with What We Deliver */}

      {/* Modernization Roadmap */}
      {timelineEntries.length > 0 && (
        <div className={`py-16 ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
          <div className="max-w-6xl mx-auto px-8">
            <div className="text-center mb-14">
              <span className={`text-sm tracking-[0.35em] uppercase ${theme === 'dark' ? 'text-cyan-300/70' : 'text-cyan-600'}`}>
                Data Modernization Roadmap
              </span>
              <h2 className={`text-4xl font-bold mt-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                From Assessment to Enterprise-Grade Platforms
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {timelineEntries.map((entry, index) => (
                <div
                  key={entry.title}
                  className={`p-8 rounded-[28px] border relative overflow-hidden ${
                    theme === 'dark'
                      ? 'bg-slate-950/60 border-cyan-500/20'
                      : 'bg-white border-cyan-100 shadow-lg'
                  }`}
                >
                  <div className={`absolute inset-0 pointer-events-none ${theme === 'dark' ? 'opacity-10' : 'opacity-5'}`} style={{
                    backgroundImage: 'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
                    backgroundSize: '30px 30px'
                  }} />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-5">
                      <div className={`text-5xl font-black ${theme === 'dark' ? 'text-cyan-500/40' : 'text-cyan-500/60'}`}>
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${theme === 'dark' ? 'bg-cyan-500/20 text-cyan-100' : 'bg-cyan-50 text-cyan-700'}`}>
                        Phase
                      </div>
                    </div>
                    <h3 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {entry.title}
                    </h3>
                    <p className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300' : 'text-gray-600'}`}>
                      {entry.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* What We Deliver & Business Impact - Side by side */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* What We Deliver */}
            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-cyan-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  What We Deliver
                </h3>
              </div>
              <div className="space-y-6">
                {service.keyFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start group">
                    <CheckCircle2 className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Impact */}
            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-emerald-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Business Impact
                </h3>
              </div>
              <div className="space-y-6">
                {service.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start group">
                    <TrendingUp className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use Cases - Masonry-style grid */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="h-1 w-20 bg-cyan-500 mx-auto mb-6"></div>
            <h2 className={`text-4xl lg:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Strategic Applications
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.useCases.map((useCase, i) => (
              <div 
                key={i} 
                className={`group p-8 border-l-4 border-cyan-500 transition-all duration-300 hover:border-l-8 ${
                  theme === 'dark' 
                    ? 'bg-slate-900/50 hover:bg-slate-900' 
                    : 'bg-gray-50 hover:bg-white hover:shadow-lg'
                }`}
              >
                <div className="flex items-start">
                  <div className={`text-6xl font-bold mr-4 opacity-20 ${theme === 'dark' ? 'text-cyan-500' : 'text-cyan-600'}`}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <p className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                    {useCase}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA - Bold, executive style */}
      <div className={`py-24 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'}`}>
        <div className="max-w-5xl mx-auto px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready For AI-Ready Data?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Establish governed data foundations for better decisions and production AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button 
              onClick={() => navigate('/contact')} 
              className="px-16 py-6 bg-cyan-500 hover:bg-cyan-600 text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Schedule Strategy Session
            </button>
            <button 
              onClick={() => navigate('/services')} 
              className="px-16 py-6 border-2 border-white text-white hover:bg-white hover:text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Explore All Services
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataAdvisory;